// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import axios from 'axios'
import { Contract } from 'ethers'
import { DefiCollateralCTO } from './CollateralSetCTO'
import { Erc20Wei } from './conversions'
import { DefiData } from './defiCTO'
import { differenceLessThanVariance, getMainnetProviderForDefi, TimeStructure } from './defiVaultUtils'
import { ChainId, EXCHANGE_RATE_ONE } from './Enums'
import { logOT } from './loggingShared'
import { MORPHO_ABI } from './morphoAbi'
export async function getMorphoNameAndSymbol(
  collateralCTO: DefiCollateralCTO
): Promise<{ name: string; symbol: string }> {
  const { defiVaultAddr } = collateralCTO
  const gqlMorphoQueryVaultInfo = `query VaultInfo {
    vaultByAddress(address: "${defiVaultAddr}", chainId: ${collateralCTO.defiChainId}) {
        id
        address
        symbol
        name
    
        dailyApy
        monthlyApy
        weeklyApy
        state {      
            netApyWithoutRewards
       } 
       
        dailyApys {
            apy
        }
        weeklyApys {
            apy
        }
   
    }
}`
  await process.nextTick(() => {})
  const retVaultInfo = await axios.post('https://api.morpho.org/graphql', { query: gqlMorphoQueryVaultInfo })

  const vaultByAddress = retVaultInfo.data.data.vaultByAddress
  const symbol = vaultByAddress.symbol
  const name = vaultByAddress.name

  logOT.info({
    tag: 'Morpho Vault Info',

    name,
    symbol,
    gqlMorphoQueryVaultInfo,
    defiVaultAddr,
  })
  return { name, symbol }
}

async function getMorphoVaultInterestRate(
  collateralCTO: DefiCollateralCTO,
  timeStructure?: TimeStructure
): Promise<{ interestRate: number }> {
  const { defiVaultAddr } = collateralCTO
  if (timeStructure) {
    return { interestRate: 0 }
  }

  const gqlMorphoQueryVaultInfo = `query VaultInfo {
    vaultByAddress(address: "${defiVaultAddr}", chainId: ${collateralCTO.defiChainId}) {
        id
        address
        symbol
        name
    
        dailyApy
        monthlyApy
        weeklyApy
        state {      
            netApyWithoutRewards
       } 
       
        dailyApys {
            apy
        }
        weeklyApys {
            apy
        }
   
    }
}`

  await process.nextTick(() => {})
  const retVaultInfo = await axios.post('https://api.morpho.org/graphql', { query: gqlMorphoQueryVaultInfo })

  const vaultByAddress = retVaultInfo.data.data.vaultByAddress

  const interestRate = vaultByAddress.state.netApyWithoutRewards
  logOT.info({
    tag: 'Morpho Vault Info',

    interestRate,
    gqlMorphoQueryVaultInfo,
    defiVaultAddr,
  })
  return { interestRate }
}

async function getMorphoNativeDefiExchangeRate(
  collateralCTO: DefiCollateralCTO,
  timeStructure?: TimeStructure
): Promise<{
  nativeDefiExchangeRateBeforeFees: bigint

  defiTotalShares: bigint
  defiTotalAssets: bigint
}> {
  const { defiVaultAddr } = collateralCTO
  const gqlMorphoQueryVaultByAddress = `query VaultByAddress {
    vaultByAddress(address: "${defiVaultAddr}", chainId: ${collateralCTO.defiChainId}) {
       
        state { 
            totalAssets
            totalSupply
            sharePrice
        }
    }
}`
  await process.nextTick(() => {})
  const retVaultByAddress = await axios.post('https://api.morpho.org/graphql', { query: gqlMorphoQueryVaultByAddress })

  logOT.info({
    gqlMorphoQueryVaultByAddress,
    defiVaultAddr,
    retVaultByAddress: JSON.stringify(retVaultByAddress.data, null, 2),
  })

  const vaultByAddress = retVaultByAddress.data.data.vaultByAddress

  const mainnetProvider = getMainnetProviderForDefi(collateralCTO.defiChainId)
  const morphoContract = new Contract(defiVaultAddr, MORPHO_ABI, mainnetProvider)
  let defiTotalShares
  let defiTotalAssets
  if (timeStructure) {
    const { blockNumberEthereum, blockNumberBase } = timeStructure
    const blockNumber = collateralCTO.defiChainId === ChainId.MAINNET ? blockNumberEthereum : blockNumberBase

    defiTotalShares = await morphoContract.totalSupply({ blockTag: blockNumber })
    defiTotalAssets = await morphoContract.totalAssets({ blockTag: blockNumber })
  } else {
    const defiTotalSharesGQL = BigInt(vaultByAddress.state.totalSupply)
    const defiTotalAssetsGQL = BigInt(vaultByAddress.state.totalAssets)

    defiTotalShares = await morphoContract.totalSupply()
    defiTotalAssets = await morphoContract.totalAssets()
    logOT.info('Morpho Total Shares and Assets', {
      tag: 'defiTotalSharesGQL',
      defiTotalSharesGQL,
    })
    logOT.info('defiTotalShares', defiTotalShares)
    logOT.info('differenceLessThanVariance', defiTotalSharesGQL - defiTotalShares)
    if (!differenceLessThanVariance(defiTotalSharesGQL, defiTotalShares, 100000n)) {
      logOT.error('Morpho Total Shares mismatch')
    }
    logOT.info('defiTotalAssetsGQL', defiTotalAssetsGQL)
    logOT.info('defiTotalAssets', defiTotalAssets)
    logOT.info('differenceLessThanVariance', defiTotalAssetsGQL - defiTotalAssets)
    if (!differenceLessThanVariance(defiTotalAssetsGQL, defiTotalAssets, 50000000n)) {
      logOT.error('Morpho Total Assets mismatch')
    }
  }

  const nativeDefiExchangeRateUnScaled: bigint =
    (BigInt(defiTotalAssets) * BigInt(EXCHANGE_RATE_ONE)) / BigInt(defiTotalShares)

  const nativeDefiExchangeRateBeforeFees: bigint =
    (BigInt(nativeDefiExchangeRateUnScaled) * BigInt(collateralCTO.nativeDefiExchangeRateScale)) /
    1000000_000000_000000n

  logOT.info({
    tag: 'Morpho Native Defi Exchange Rate ',
    nativeDefiExchangeRate: nativeDefiExchangeRateUnScaled,
    nativeDefiExchangeRateBeforeFees,
    defiTotalShares,
    defiTotalAssets,
  })
  return {
    nativeDefiExchangeRateBeforeFees,
    defiTotalShares,
    defiTotalAssets,
  }
}
async function getMorphoTransferWalletAssets(
  collateralCTO: DefiCollateralCTO,
  timeStructure?: TimeStructure
): Promise<{ defiOTShares: bigint; defiOTAssets: bigint }> {
  const { defiVaultAddr, transferWalletAddr, calibrationWalletAddr } = collateralCTO

  const gqlMorphoQueryVaultPosition = `query VaultPositionTransfer {
    vaultPosition(
        userAddress: "${transferWalletAddr}"
        vaultAddress: "${defiVaultAddr}"
    ) {
        assets
        shares
    }
   }
  `

  await process.nextTick(() => {})
  const retVaultByPosition = await axios.post('https://api.morpho.org/graphql', {
    query: gqlMorphoQueryVaultPosition,
  })
  logOT.info({
    gqlMorphoQueryVaultPosition,
    transferWalletAddr,
    defiVaultAddr,
    retVaultByAddress: retVaultByPosition.data,
  })

  let defiOTShares
  let defiOTAssets
  const mainnetProvider = getMainnetProviderForDefi(collateralCTO.defiChainId)
  const morphoContract = new Contract(defiVaultAddr, MORPHO_ABI, mainnetProvider)
  if (timeStructure) {
    const { blockNumberEthereum } = timeStructure

    defiOTShares = await morphoContract.balanceOf(transferWalletAddr, { blockTag: blockNumberEthereum })
    defiOTAssets = await morphoContract.convertToAssets(defiOTShares, { blockTag: blockNumberEthereum })
  } else {
    defiOTShares = await morphoContract.balanceOf(transferWalletAddr)
    defiOTAssets = await morphoContract.convertToAssets(defiOTShares)
    if (retVaultByPosition.data.data) {
      const vaultPosition = retVaultByPosition.data.data.vaultPosition
      const _defiOTSharesGQL = BigInt(vaultPosition.shares)
      const _defiOTAssetsGQL = BigInt(vaultPosition.assets)
      // if (!differenceLessThanVariance(defiOTSharesGQL, defiOTShares, 100000n)) {
      //   throw new Error('Morpho Total Shares mismatch')
      // }
      // if (!differenceLessThanVariance(defiOTAssetsGQL, defiOTAssets, 100000n)) {
      //   throw new Error('Morpho Total Assets mismatch')
      // }
    }
  }

  logOT.info({
    tag: 'Morpho Calibration Assets',
    defiOTShares,
    defiOTAssets,
    gqlMorphoQueryVaultPosition,
    calibrationWalletAddr,
    defiVaultAddr,
  })
  return { defiOTShares, defiOTAssets }
}

async function getMorphoCalibrationAssets(
  collateralCTO: DefiCollateralCTO,
  timeStructure?: TimeStructure
): Promise<{ calibrationAssets: bigint; calibratedExchangeRateBeforeFees: bigint }> {
  const { defiVaultAddr, calibrationWalletAddr } = collateralCTO
  const gqlMorphoQueryCalibration = `query VaultPositionCalibration {
      vaultPosition(
          userAddress: "${calibrationWalletAddr}"
          vaultAddress: "${defiVaultAddr}"
          chainId: ${collateralCTO.defiChainId}
      ) {
          assets
          shares
      }
    }
  `
  await process.nextTick(() => {})
  const retCalibration = await axios.post('https://api.morpho.org/graphql', { query: gqlMorphoQueryCalibration })

  const mainnetProvider = getMainnetProviderForDefi(collateralCTO.defiChainId)
  const morphoContract = new Contract(defiVaultAddr, MORPHO_ABI, mainnetProvider)
  if (timeStructure) {
    const { blockNumberEthereum } = timeStructure

    const calibrationShares = await morphoContract.balanceOf(calibrationWalletAddr, { blockTag: blockNumberEthereum })
    const calibrationAssets = await morphoContract.convertToAssets(calibrationShares, { blockTag: blockNumberEthereum })
    const calibratedExchangeRateBeforeFees = (calibrationAssets * BigInt(EXCHANGE_RATE_ONE)) / 1000_000000n
    logOT.info({
      tag: 'Morpho Calibration Assets',
      calibrationAssets,
      calibratedExchangeRateBeforeFees,
      gqlMorphoQueryCalibration,
      calibrationWalletAddr,
      defiVaultAddr,
    })
    return { calibrationAssets, calibratedExchangeRateBeforeFees }
  } else {
    const calibrationPositionGQL = retCalibration.data.data?.vaultPosition
    const calibrationAssetsGQL: bigint = BigInt(calibrationPositionGQL?.assets || '0')
    const _calibratedExchangeRateGQL: bigint = (calibrationAssetsGQL * BigInt(EXCHANGE_RATE_ONE)) / 1000_000000n

    const calibrationShares = await morphoContract.balanceOf(calibrationWalletAddr)
    const calibrationAssets = await morphoContract.convertToAssets(calibrationShares)
    const calibratedExchangeRateBeforeFees = (calibrationAssets * BigInt(EXCHANGE_RATE_ONE)) / 1000_000000n
    // if (!differenceLessThanVariance(calibrationAssetsGQL, calibrationAssets, 50000000n)) {
    //   throw new Error('Morpho Calibration Assets mismatch')
    // }
    // if (!differenceLessThanVariance(calibratedExchangeRateGQL, calibratedExchangeRateBeforeFees, 500000000000n)) {
    //   throw new Error('Morpho Calibration Exchange Rate mismatch')
    // }
    logOT.info({
      tag: 'Morpho Calibration Assets',
      calibrationAssets,
      calibratedExchangeRateBeforeFees,
      gqlMorphoQueryCalibration,
      calibrationWalletAddr,
      defiVaultAddr,
    })
    return { calibrationAssets, calibratedExchangeRateBeforeFees }
  }
}

export async function getDefiDataMorpho(
  collateralCTO: DefiCollateralCTO,
  timeStructure?: TimeStructure
): Promise<DefiData> {
  logOT.info('getDefiDataMorpho', timeStructure)

  const { interestRate } = await getMorphoVaultInterestRate(collateralCTO, timeStructure)
  logOT.info('getDefiDataMorpho', { interestRate })
  const { nativeDefiExchangeRateBeforeFees, defiTotalShares, defiTotalAssets } = await getMorphoNativeDefiExchangeRate(
    collateralCTO,
    timeStructure
  )
  logOT.info('getDefiDataMorpho', {
    nativeDefiExchangeRateBeforeFees,
    defiTotalShares,
    defiTotalAssets,
    timeStructure,
  })
  const { defiOTShares, defiOTAssets } = await getMorphoTransferWalletAssets(collateralCTO, timeStructure)
  logOT.info('getDefiDataMorpho', defiOTShares, defiOTAssets)
  const { calibrationAssets, calibratedExchangeRateBeforeFees } = await getMorphoCalibrationAssets(
    collateralCTO,
    timeStructure
  )
  logOT.info('getDefiDataMorpho', calibrationAssets, calibratedExchangeRateBeforeFees)
  const morphoRetValue: DefiData = {
    defiType: collateralCTO.defiType,
    interestRate,

    defiOTShares: Erc20Wei(defiOTShares),
    defiOTAssets: Erc20Wei(defiOTAssets),
    defiTotalShares: Erc20Wei(defiTotalShares),
    defiTotalAssets: Erc20Wei(defiTotalAssets),

    decimals: 6,
    calibratedBalance: Erc20Wei(calibrationAssets),
    calibratedExchangeRateBeforeFees: Erc20Wei(calibratedExchangeRateBeforeFees),
    nativeDefiExchangeRateBeforeFees: Erc20Wei(nativeDefiExchangeRateBeforeFees),

    collateralCTO: collateralCTO,
  }
  logOT.info('Morpho GQL return', { tag: 'Morpho  return', morphoRetValue })
  return morphoRetValue
}
